import React from 'react';
import { ArrowDown, ArrowUp, AudioLines, FileOutput, FileVideo, Plus, Scissors, Trash2 } from 'lucide-react';
import { getPreset, type FilterPreset } from '../data/filterCatalog';
import type { NodeData, StreamType, WorkflowDocument, WorkflowNode } from '../nodes/NodeTypes';
import { orderedStreamFilters } from '../lib/workflowGraph';
import NodeFields from './NodeFields';

interface BeginnerWorkspaceProps {
  workflow: WorkflowDocument;
  onUpdate: (nodeId: string, patch: Partial<NodeData>) => void;
  onAddPreset: (preset: FilterPreset) => void;
  onDelete: (nodeId: string) => void;
  onMove: (nodeId: string, streamType: StreamType, direction: -1 | 1) => void;
}

function StepRow({ node, index, count, onUpdate, onDelete, onMove }: {
  node: WorkflowNode; index: number; count: number; onUpdate: BeginnerWorkspaceProps['onUpdate']; onDelete: BeginnerWorkspaceProps['onDelete']; onMove: BeginnerWorkspaceProps['onMove'];
}) {
  const streamType = node.data.streamType || 'video';
  return (
    <article className={'beginner-step beginner-step--' + streamType}>
      <span className="beginner-step__number">{String(index + 1).padStart(2, '0')}</span>
      <div className="beginner-step__body">
        <div className="beginner-step__heading">
          <strong>{node.data.label}</strong>
          <span>{node.data.category || (streamType === 'video' ? '画面处理' : '声音处理')}</span>
        </div>
        <NodeFields node={node} onChange={onUpdate} />
      </div>
      <div className="beginner-step__actions">
        <button type="button" className="icon-button" onClick={() => onMove(node.id, streamType, -1)} disabled={index === 0} title="上移步骤" aria-label="上移步骤"><ArrowUp size={15} /></button>
        <button type="button" className="icon-button" onClick={() => onMove(node.id, streamType, 1)} disabled={index === count - 1} title="下移步骤" aria-label="下移步骤"><ArrowDown size={15} /></button>
        <button type="button" className="icon-button icon-button--danger" onClick={() => onDelete(node.id)} title="移除步骤" aria-label="移除步骤"><Trash2 size={15} /></button>
      </div>
    </article>
  );
}

function StreamLane({ streamType, nodes, onUpdate, onAddPreset, onDelete, onMove }: {
  streamType: StreamType; nodes: WorkflowNode[]; onUpdate: BeginnerWorkspaceProps['onUpdate']; onAddPreset: BeginnerWorkspaceProps['onAddPreset']; onDelete: BeginnerWorkspaceProps['onDelete']; onMove: BeginnerWorkspaceProps['onMove'];
}) {
  const isVideo = streamType === 'video';
  return (
    <section className={'stream-lane stream-lane--' + streamType}>
      <div className="stream-lane__header">
        <span className="stream-lane__icon">{isVideo ? <FileVideo size={17} /> : <AudioLines size={17} />}</span>
        <div><h3>{isVideo ? '视频轨道' : '音频轨道'}</h3><span>{nodes.length} 个处理步骤</span></div>
      </div>
      <div className="stream-lane__steps">
        {nodes.map((node, index) => (
          <StepRow key={node.id} node={node} index={index} count={nodes.length} onUpdate={onUpdate} onDelete={onDelete} onMove={onMove} />
        ))}
        {!nodes.length && <p className="lane-empty">{isVideo ? '视频将直接传递到输出。' : '音频将直接传递到输出。'}</p>}
        <button type="button" className="add-step-row" onClick={() => {
          const preset = getPreset(isVideo ? 'scale' : 'volume');
          if (preset) onAddPreset(preset);
        }}>
          <Plus size={15} /> 添加{isVideo ? '视频' : '音频'}步骤
        </button>
      </div>
    </section>
  );
}

const BeginnerWorkspace: React.FC<BeginnerWorkspaceProps> = ({ workflow, onUpdate, onAddPreset, onDelete, onMove }) => {
  const input = workflow.nodes.find((node) => node.type === 'input');
  const output = workflow.nodes.find((node) => node.type === 'output');
  const videoFilters = orderedStreamFilters(workflow, 'video');
  const audioFilters = orderedStreamFilters(workflow, 'audio');

  return (
    <div className="beginner-workspace">
      <div className="beginner-workspace__intro">
        <div>
          <h1>处理流程</h1>
          <p>从素材开始，按顺序添加画面与声音处理。</p>
        </div>
        <div className="workflow-path" aria-label="输入到输出">
          <span><FileVideo size={15} />输入</span><i />
          <span><Scissors size={15} />{videoFilters.length + audioFilters.length} 步</span><i />
          <span><FileOutput size={15} />输出</span>
        </div>
      </div>
      <div className="source-output-grid">
        {input && (
          <section className="workflow-form-section">
            <div className="workflow-section-heading"><span className="section-icon section-icon--source"><FileVideo size={17} /></span><div><h2>输入素材</h2><p>设置源文件和可选的入点</p></div></div>
            <NodeFields node={input} onChange={onUpdate} />
          </section>
        )}
        {output && (
          <section className="workflow-form-section workflow-form-section--output">
            <div className="workflow-section-heading"><span className="section-icon section-icon--output"><FileOutput size={17} /></span><div><h2>导出设置</h2><p>路径、编码器与画质</p></div></div>
            <NodeFields node={output} onChange={onUpdate} />
          </section>
        )}
      </div>
      <div className="beginner-workspace__lanes">
        <StreamLane streamType="video" nodes={videoFilters} onUpdate={onUpdate} onAddPreset={onAddPreset} onDelete={onDelete} onMove={onMove} />
        <StreamLane streamType="audio" nodes={audioFilters} onUpdate={onUpdate} onAddPreset={onAddPreset} onDelete={onDelete} onMove={onMove} />
      </div>
      <p className="beginner-note">复杂合成、分支和多输入效果可切换到高级模式编辑。</p>
    </div>
  );
};

export default BeginnerWorkspace;
